/**
 * Paranorm testimonials carousel for Shopify Dawn 15.5.
 *
 * Required class structure:
 * .pn-testimonials
 *   .pn-testimonials__carousel
 *     .pn-testimonials__viewport
 *       .pn-testimonials__track
 *         .pn-review-column (one or more direct children)
 *     .pn-testimonials__arrow--prev (optional)
 *     .pn-testimonials__arrow--next (optional)
 *
 * Optional attributes on .pn-testimonials:
 * data-autoplay="false"       Disables autoplay (enabled by default).
 * data-autoplay-delay="6000"  Delay in milliseconds (default: 5000).
 * data-carousel-label="..."   Accessible carousel label.
 */
(function () {
  'use strict';

  const ROOT = '.pn-testimonials';
  const VIEWPORT = '.pn-testimonials__viewport';
  const TRACK = '.pn-testimonials__track';
  const PREVIOUS = '.pn-testimonials__arrow--prev';
  const NEXT = '.pn-testimonials__arrow--next';
  const CLONE = 'data-pn-testimonials-clone';
  const INSTANCE = '__pnTestimonialsInstance';
  let instanceNumber = 0;

  const array = (value) => Array.prototype.slice.call(value || []);
  const mod = (value, divisor) => ((value % divisor) + divisor) % divisor;

  function saveAttribute(element, name) {
    return {
      present: element.hasAttribute(name),
      value: element.getAttribute(name)
    };
  }

  function restoreAttribute(element, name, state) {
    if (!state) return;

    if (state.present) element.setAttribute(name, state.value);
    else element.removeAttribute(name);
  }

  function setInert(element, value) {
    if ('inert' in element) element.inert = value;
  }

  function focusableElements(element) {
    return array(
      element.querySelectorAll(
        'a[href], area[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"]'
      )
    ).filter((item) => !item.hasAttribute('disabled'));
  }

  function makeClone(slide) {
    const clone = slide.cloneNode(true);

    clone.setAttribute(CLONE, '');
    clone.setAttribute('aria-hidden', 'true');
    setInert(clone, true);

    clone.removeAttribute('id');
    array(clone.querySelectorAll('[id]')).forEach((element) =>
      element.removeAttribute('id')
    );

    focusableElements(clone).forEach((element) =>
      element.setAttribute('tabindex', '-1')
    );

    return clone;
  }

  function rootsWithin(scope) {
    if (!scope || (scope.nodeType !== 1 && scope.nodeType !== 9)) return [];

    const roots = scope.nodeType === 1 && scope.matches(ROOT) ? [scope] : [];
    return roots.concat(array(scope.querySelectorAll(ROOT)));
  }

  function rootForTarget(target) {
    if (!target || target.nodeType !== 1) return null;
    return target.closest(ROOT) || target.querySelector(ROOT);
  }

  class ParanormTestimonials {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(VIEWPORT);
      this.track = root.querySelector(TRACK);
      this.previousButton = root.querySelector(PREVIOUS);
      this.nextButton = root.querySelector(NEXT);
      this.id = ++instanceNumber;

      this.originalSlides = [];
      this.slides = [];
      this.offsets = [];
      this.slideStates = [];
      this.listeners = [];
      this.pauseReasons = new Set();

      this.logicalIndex = 0;
      this.physicalIndex = 0;
      this.isAnimating = false;
      this.destroyed = false;
      this.pointer = null;

      this.resizeFrame = null;
      this.autoplayTimer = null;
      this.transitionTimer = null;
      this.focusTimer = null;
      this.suppressClickUntil = 0;
      this.resizeObserver = null;
      this.announcer = null;

      this.reducedMotion = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

      this.prefersReducedMotion = Boolean(
        this.reducedMotion && this.reducedMotion.matches
      );

      this.autoplayEnabled =
        root.dataset.autoplay !== 'false' && root.dataset.autoplay !== '0';

      this.autoplayDelay = Math.max(
        1500,
        Number.parseInt(root.dataset.autoplayDelay, 10) || 5000
      );

      this.rootState = {
        role: saveAttribute(root, 'role'),
        roleDescription: saveAttribute(root, 'aria-roledescription'),
        label: saveAttribute(root, 'aria-label')
      };

      this.viewportState = {
        id: saveAttribute(this.viewport, 'id'),
        tabindex: saveAttribute(this.viewport, 'tabindex'),
        live: saveAttribute(this.viewport, 'aria-live'),
        touchAction: this.viewport.style.touchAction
      };

      this.trackState = {
        transform: this.track.style.transform,
        transition: this.track.style.transition,
        transitionProperty: this.track.style.transitionProperty,
        transitionDuration: this.track.style.transitionDuration,
        transitionTimingFunction: this.track.style.transitionTimingFunction,
        willChange: this.track.style.willChange
      };

      this.buttonStates = [];

      this.initialize();
    }

    listen(element, event, callback, options) {
      element.addEventListener(event, callback, options);
      this.listeners.push({ element, event, callback, options });
    }

    initialize() {
      this.originalSlides = array(this.track.children).filter(
        (slide) => !slide.hasAttribute(CLONE)
      );

      if (!this.originalSlides.length) return;

      this.setupAccessibility();
      this.createClones();

      this.slides = array(this.track.children);
      this.measure();

      this.logicalIndex = 0;
      this.physicalIndex =
        this.originalSlides.length > 1 ? this.originalSlides.length : 0;

      this.setActiveSlide(0);
      this.setPosition(this.physicalIndex, true);
      this.updateControls();
      this.bindEvents();
      this.queueAutoplay();
    }

    setupAccessibility() {
      const viewportId =
        this.viewport.id || `pn-testimonials-viewport-${this.id}`;

      const label =
        this.root.dataset.carouselLabel || 'Customer testimonials';

      if (!this.root.hasAttribute('role')) {
        this.root.setAttribute('role', 'region');
      }

      this.root.setAttribute('aria-roledescription', 'carousel');

      if (!this.root.hasAttribute('aria-label')) {
        this.root.setAttribute('aria-label', label);
      }

      this.viewport.id = viewportId;
      this.viewport.setAttribute('aria-live', 'off');

      if (!this.viewport.hasAttribute('tabindex')) {
        this.viewport.tabIndex = 0;
      }

      this.viewport.style.touchAction = 'pan-y';

      this.announcer = document.createElement('p');
      this.announcer.className = 'pn-testimonials__status visually-hidden';
      this.announcer.setAttribute('aria-live', 'polite');
      this.announcer.setAttribute('aria-atomic', 'true');
      this.announcer.style.cssText = [
        'position:absolute',
        'width:1px',
        'height:1px',
        'padding:0',
        'margin:-1px',
        'overflow:hidden',
        'clip:rect(0,0,0,0)',
        'white-space:nowrap',
        'border:0'
      ].join(';');

      this.root.appendChild(this.announcer);

      this.setupButton(
        this.previousButton,
        'Previous testimonials',
        viewportId
      );

      this.setupButton(this.nextButton, 'Next testimonials', viewportId);

      this.slideStates = this.originalSlides.map((slide) => ({
        slide,
        ariaHidden: saveAttribute(slide, 'aria-hidden'),
        inert: saveAttribute(slide, 'inert'),
        role: saveAttribute(slide, 'role'),
        label: saveAttribute(slide, 'aria-label'),
        focusables: focusableElements(slide).map((element) => ({
          element,
          tabindex: saveAttribute(element, 'tabindex')
        }))
      }));

      this.slideStates.forEach((state, index) => {
        if (!state.slide.hasAttribute('role')) {
          state.slide.setAttribute('role', 'group');
        }

        if (!state.slide.hasAttribute('aria-label')) {
          state.slide.setAttribute(
            'aria-label',
            `Testimonial group ${index + 1} of ${this.slideStates.length}`
          );
        }
      });
    }

    setupButton(button, label, viewportId) {
      if (!button) return;

      this.buttonStates.push({
        button,
        type: saveAttribute(button, 'type'),
        label: saveAttribute(button, 'aria-label'),
        controls: saveAttribute(button, 'aria-controls'),
        disabledDescription: saveAttribute(button, 'aria-disabled'),
        disabled: 'disabled' in button ? button.disabled : undefined
      });

      if (button.tagName === 'BUTTON' && !button.hasAttribute('type')) {
        button.type = 'button';
      }

      if (!button.hasAttribute('aria-label')) {
        button.setAttribute('aria-label', label);
      }

      button.setAttribute('aria-controls', viewportId);
    }

    createClones() {
      if (this.originalSlides.length < 2) return;

      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();

      this.originalSlides.forEach((slide) => {
        before.appendChild(makeClone(slide));
        after.appendChild(makeClone(slide));
      });

      this.track.insertBefore(before, this.originalSlides[0]);
      this.track.appendChild(after);
    }

    measure() {
      this.offsets = this.slides.map((slide) => slide.offsetLeft);
    }

    setPosition(index, immediate) {
      if (!this.offsets.length) return;

      const safeIndex = Math.max(
        0,
        Math.min(index, this.offsets.length - 1)
      );

      if (immediate) this.track.style.transition = 'none';

      this.track.style.transform = `translate3d(${
        -this.offsets[safeIndex]
      }px, 0, 0)`;

      if (immediate) {
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }
    }

    setActiveSlide(logicalIndex) {
      const activeIndex = mod(
        logicalIndex,
        this.slideStates.length || 1
      );

      this.slideStates.forEach((state, index) => {
        const active = index === activeIndex;

        if (active) {
          restoreAttribute(state.slide, 'aria-hidden', state.ariaHidden);
          restoreAttribute(state.slide, 'inert', state.inert);
          setInert(state.slide, state.inert.present);
        } else {
          state.slide.setAttribute('aria-hidden', 'true');
          state.slide.setAttribute('inert', '');
          setInert(state.slide, true);
        }

        state.focusables.forEach((focusable) => {
          if (active) {
            restoreAttribute(
              focusable.element,
              'tabindex',
              focusable.tabindex
            );
          } else {
            focusable.element.setAttribute('tabindex', '-1');
          }
        });
      });
    }

    updateControls() {
      const disabled = this.originalSlides.length < 2;

      [this.previousButton, this.nextButton].forEach((button) => {
        if (!button) return;

        button.setAttribute('aria-disabled', String(disabled));

        if ('disabled' in button) button.disabled = disabled;
      });
    }

    bindEvents() {
      if (this.previousButton) {
        this.listen(this.previousButton, 'click', (event) => {
          event.preventDefault();
          this.moveBy(-1, 'button');
        });
      }

      if (this.nextButton) {
        this.listen(this.nextButton, 'click', (event) => {
          event.preventDefault();
          this.moveBy(1, 'button');
        });
      }

      this.listen(this.viewport, 'keydown', (event) => {
        if (
          event.target.matches(
            'input, textarea, select, [contenteditable="true"]'
          )
        ) {
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.moveBy(-1, 'keyboard');
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.moveBy(1, 'keyboard');
        }
      });

      this.listen(this.root, 'mouseenter', () => this.pause('hover'));
      this.listen(this.root, 'mouseleave', () => this.resume('hover'));

      this.listen(this.root, 'focusin', () => this.pause('focus'));

      this.listen(this.root, 'focusout', () => {
        window.clearTimeout(this.focusTimer);

        this.focusTimer = window.setTimeout(() => {
          if (!this.root.contains(document.activeElement)) {
            this.resume('focus');
          }
        }, 0);
      });

      this.listen(this.viewport, 'pointerdown', (event) =>
        this.pointerDown(event)
      );

      this.listen(this.viewport, 'pointermove', (event) =>
        this.pointerMove(event)
      );

      this.listen(this.viewport, 'pointerup', (event) =>
        this.pointerUp(event)
      );

      this.listen(this.viewport, 'pointercancel', (event) =>
        this.pointerCancel(event)
      );

      this.listen(this.viewport, 'lostpointercapture', (event) =>
        this.pointerCancel(event)
      );

      this.listen(
        this.viewport,
        'click',
        (event) => {
          if (performance.now() < this.suppressClickUntil) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );

      this.listen(this.track, 'transitionend', (event) => {
        if (
          event.target === this.track &&
          event.propertyName === 'transform'
        ) {
          this.completeTransition();
        }
      });

      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.pause('visibility');
        else this.resume('visibility');
      });

      if (this.reducedMotion) {
        const onMotionChange = (event) => {
          this.prefersReducedMotion = event.matches;
          this.clearAutoplay();

          if (this.prefersReducedMotion && this.isAnimating) {
            this.setPosition(this.physicalIndex, true);
            this.isAnimating = false;
          }

          this.queueAutoplay();
        };

        if (this.reducedMotion.addEventListener) {
          this.listen(this.reducedMotion, 'change', onMotionChange);
        } else {
          this.reducedMotion.addListener(onMotionChange);

          this.listeners.push({
            mediaListener: true,
            element: this.reducedMotion,
            callback: onMotionChange
          });
        }
      }

      this.bindResizeHandling();
    }

    bindResizeHandling() {
      const scheduleMeasure = () => {
        if (this.resizeFrame) {
          window.cancelAnimationFrame(this.resizeFrame);
        }

        this.resizeFrame = window.requestAnimationFrame(() => {
          this.resizeFrame = null;

          if (this.isAnimating) return;

          this.measure();
          this.setPosition(this.physicalIndex, true);
        });
      };

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(scheduleMeasure);
        this.resizeObserver.observe(this.viewport);
      } else {
        this.listen(window, 'resize', scheduleMeasure);
      }
    }

    pointerDown(event) {
      if (
        this.originalSlides.length < 2 ||
        this.isAnimating ||
        event.isPrimary === false ||
        (event.pointerType === 'mouse' && event.button !== 0)
      ) {
        return;
      }

      this.pointer = {
        id: event.pointerId,
        startX: event.clientX,
        lastX: event.clientX,
        lastTime: performance.now(),
        velocity: 0,
        moved: false
      };

      this.pause('drag');
      this.track.style.transition = 'none';

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture is unavailable in some embedded browsers.
      }
    }

    pointerMove(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;

      const now = performance.now();
      const distance = event.clientX - this.pointer.startX;

      this.pointer.velocity =
        (event.clientX - this.pointer.lastX) /
        Math.max(1, now - this.pointer.lastTime);

      this.pointer.lastX = event.clientX;
      this.pointer.lastTime = now;
      this.pointer.moved =
        this.pointer.moved || Math.abs(distance) > 6;

      if (this.pointer.moved) event.preventDefault();

      this.track.style.transform = `translate3d(${
        -this.offsets[this.physicalIndex] + distance
      }px, 0, 0)`;
    }

    pointerUp(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;

      const pointer = this.pointer;
      const distance = event.clientX - pointer.startX;
      const threshold = Math.min(
        100,
        this.viewport.clientWidth * 0.18
      );

      const shouldMove =
        Math.abs(distance) >= threshold ||
        Math.abs(pointer.velocity) > 0.55;

      this.pointer = null;

      try {
        this.viewport.releasePointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture may already have been released.
      }

      if (pointer.moved) {
        this.suppressClickUntil = performance.now() + 350;
      }

      if (shouldMove) {
        this.moveBy(distance > 0 ? -1 : 1, 'drag');
      } else {
        this.snapBack();
      }

      this.resume('drag');
    }

    pointerCancel(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;

      this.pointer = null;
      this.snapBack();
      this.resume('drag');
    }

    snapBack() {
      this.track.style.transition = '';
      this.track.style.transitionProperty = 'transform';
      this.track.style.transitionDuration = '260ms';
      this.track.style.transitionTimingFunction = 'ease-out';

      this.setPosition(this.physicalIndex, false);
    }

    moveBy(amount, source) {
      if (
        this.destroyed ||
        this.isAnimating ||
        this.originalSlides.length < 2
      ) {
        return false;
      }

      const direction = amount < 0 ? -1 : 1;

      this.logicalIndex = mod(
        this.logicalIndex + direction,
        this.originalSlides.length
      );

      this.physicalIndex += direction;
      this.setActiveSlide(this.logicalIndex);

      if (source !== 'autoplay') {
        this.announce(
          `Testimonial group ${this.logicalIndex + 1} of ${
            this.originalSlides.length
          }`
        );
      }

      if (this.prefersReducedMotion) {
        this.setPosition(this.physicalIndex, true);
        this.completeTransition(true);
        return true;
      }

      this.isAnimating = true;
      this.track.style.transition = '';
      this.track.style.transitionProperty = 'transform';
      this.track.style.transitionDuration = '460ms';
      this.track.style.transitionTimingFunction =
        'cubic-bezier(0.22, 0.61, 0.36, 1)';

      window.requestAnimationFrame(() => {
        if (!this.destroyed) {
          this.setPosition(this.physicalIndex, false);
        }
      });

      window.clearTimeout(this.transitionTimer);

      this.transitionTimer = window.setTimeout(
        () => this.completeTransition(),
        560
      );

      return true;
    }

    completeTransition(force) {
      if (this.destroyed) return;

      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;

      if (!this.isAnimating && !force) return;

      this.isAnimating = false;

      if (this.originalSlides.length > 1) {
        if (this.physicalIndex < this.originalSlides.length) {
          this.physicalIndex += this.originalSlides.length;
          this.setPosition(this.physicalIndex, true);
        } else if (
          this.physicalIndex >= this.originalSlides.length * 2
        ) {
          this.physicalIndex -= this.originalSlides.length;
          this.setPosition(this.physicalIndex, true);
        }
      }

      this.measure();
      this.setPosition(this.physicalIndex, true);
      this.queueAutoplay();
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.clearAutoplay();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.queueAutoplay();
    }

    clearAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    queueAutoplay() {
      this.clearAutoplay();

      if (
        this.destroyed ||
        !this.autoplayEnabled ||
        this.prefersReducedMotion ||
        this.originalSlides.length < 2 ||
        this.pauseReasons.size ||
        this.isAnimating ||
        document.hidden
      ) {
        return;
      }

      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;

        if (!this.moveBy(1, 'autoplay')) {
          this.queueAutoplay();
        }
      }, this.autoplayDelay);
    }

    announce(message) {
      if (!this.announcer) return;

      this.announcer.textContent = '';

      window.requestAnimationFrame(() => {
        if (this.announcer) {
          this.announcer.textContent = message;
        }
      });
    }

    goTo(index) {
      if (!this.originalSlides.length) return;

      this.clearAutoplay();

      this.logicalIndex = mod(index, this.originalSlides.length);

      this.physicalIndex =
        this.originalSlides.length > 1
          ? this.originalSlides.length + this.logicalIndex
          : 0;

      this.isAnimating = false;
      this.setActiveSlide(this.logicalIndex);
      this.setPosition(this.physicalIndex, true);
    }

    refresh() {
      if (this.destroyed) return;

      this.measure();
      this.setPosition(this.physicalIndex, true);
    }

    destroy() {
      if (this.destroyed) return;

      this.destroyed = true;

      this.clearAutoplay();
      window.clearTimeout(this.transitionTimer);
      window.clearTimeout(this.focusTimer);

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.listeners.forEach((listener) => {
        if (listener.mediaListener) {
          listener.element.removeListener(listener.callback);
        } else {
          listener.element.removeEventListener(
            listener.event,
            listener.callback,
            listener.options
          );
        }
      });

      this.listeners = [];

      this.slideStates.forEach((state) => {
        restoreAttribute(state.slide, 'aria-hidden', state.ariaHidden);
        restoreAttribute(state.slide, 'inert', state.inert);
        restoreAttribute(state.slide, 'role', state.role);
        restoreAttribute(state.slide, 'aria-label', state.label);

        setInert(state.slide, state.inert.present);

        state.focusables.forEach((focusable) => {
          restoreAttribute(
            focusable.element,
            'tabindex',
            focusable.tabindex
          );
        });
      });

      array(this.track.querySelectorAll(`[${CLONE}]`)).forEach((clone) =>
        clone.remove()
      );

      restoreAttribute(this.root, 'role', this.rootState.role);
      restoreAttribute(
        this.root,
        'aria-roledescription',
        this.rootState.roleDescription
      );
      restoreAttribute(this.root, 'aria-label', this.rootState.label);

      restoreAttribute(this.viewport, 'id', this.viewportState.id);
      restoreAttribute(
        this.viewport,
        'tabindex',
        this.viewportState.tabindex
      );
      restoreAttribute(this.viewport, 'aria-live', this.viewportState.live);

      this.viewport.style.touchAction = this.viewportState.touchAction;

      Object.keys(this.trackState).forEach((property) => {
        this.track.style[property] = this.trackState[property];
      });

      this.buttonStates.forEach((state) => {
        restoreAttribute(state.button, 'type', state.type);
        restoreAttribute(state.button, 'aria-label', state.label);
        restoreAttribute(state.button, 'aria-controls', state.controls);
        restoreAttribute(
          state.button,
          'aria-disabled',
          state.disabledDescription
        );

        if (state.disabled !== undefined) {
          state.button.disabled = state.disabled;
        }
      });

      if (this.announcer) this.announcer.remove();

      delete this.root[INSTANCE];
    }
  }

  function init(root) {
    if (!root || root[INSTANCE]) return root && root[INSTANCE];

    if (!root.querySelector(VIEWPORT) || !root.querySelector(TRACK)) {
      return null;
    }

    const carousel = new ParanormTestimonials(root);
    root[INSTANCE] = carousel;

    return carousel;
  }

  function initWithin(scope) {
    rootsWithin(scope).forEach(init);
  }

  function destroyWithin(scope) {
    rootsWithin(scope).forEach((root) => {
      if (root[INSTANCE]) root[INSTANCE].destroy();
    });
  }

  function selectedSlideIndex(carousel, target) {
    const slide = target.closest('.pn-review-column');
    const index = carousel.originalSlides.indexOf(slide);

    return index > -1 ? index : 0;
  }

  function bindThemeEditorEvents() {
    document.addEventListener('shopify:section:load', (event) => {
      initWithin(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroyWithin(event.target);
    });

    document.addEventListener('shopify:section:select', (event) => {
      const root = rootForTarget(event.target);

      if (root && root[INSTANCE]) {
        root[INSTANCE].pause('editor');
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const root = rootForTarget(event.target);

      if (root && root[INSTANCE]) {
        root[INSTANCE].resume('editor');
      }
    });

    document.addEventListener('shopify:block:select', (event) => {
      const root = rootForTarget(event.target);
      const carousel = root && root[INSTANCE];

      if (!carousel) return;

      carousel.pause('editor');
      carousel.goTo(selectedSlideIndex(carousel, event.target));
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const root = rootForTarget(event.target);

      if (root && root[INSTANCE]) {
        root[INSTANCE].resume('editor');
      }
    });
  }

  function boot() {
    initWithin(document);
    bindThemeEditorEvents();
  }

  window.ParanormTestimonials = {
    init,
    initWithin,
    destroyWithin
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();